/**
 * maintenanceStore — recuerda que el usuario cerró el aviso de mantenimiento,
 * pero solo por HOY.
 *
 * El comportamiento pedido: se puede cerrar y vuelve a aparecer al día
 * siguiente, hasta que la ventana se haya ejecutado. Por eso no se guarda un
 * booleano "descartado" (eso lo esconde para siempre) sino la FECHA del cierre:
 * si no es la de hoy, el banner vuelve.
 *
 * La fecha se calcula en la zona horaria LOCAL del usuario a propósito. Con UTC,
 * alguien en Chile (UTC-4) que cierra el aviso a las 20:00 lo vería reaparecer
 * esa misma noche, porque en UTC ya sería el día siguiente.
 *
 * Va en su propio store y no en `themeStore` porque no es una preferencia de
 * apariencia: es el estado de un aviso operativo.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/** 'YYYY-MM-DD' en hora local. `sv-SE` da justamente ese formato. */
export function localDateKey(d: Date = new Date()): string {
  return d.toLocaleDateString('sv-SE')
}

interface MaintenanceState {
  /** { [windowId]: 'YYYY-MM-DD' } — el día en que se cerró cada aviso. */
  dismissedOn: Record<string, string>
  dismiss: (windowId: string) => void
  isDismissedToday: (windowId: string) => boolean
}

export const useMaintenanceStore = create<MaintenanceState>()(
  persist(
    (set, get) => ({
      dismissedOn: {},

      dismiss: (windowId) => set(state => {
        const hoy = localDateKey()
        // Se purgan los cierres de días anteriores al escribir: ya no sirven
        // (esos avisos vuelven a mostrarse) y sin esto el objeto crece para
        // siempre en localStorage con ids de ventanas que ya terminaron.
        const vigentes: Record<string, string> = {}
        for (const [id, fecha] of Object.entries(state.dismissedOn)) {
          if (fecha === hoy) vigentes[id] = fecha
        }
        vigentes[windowId] = hoy
        return { dismissedOn: vigentes }
      }),

      isDismissedToday: (windowId) => get().dismissedOn[windowId] === localDateKey(),
    }),
    {
      name: 'zen-maintenance',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ dismissedOn: state.dismissedOn }),
    },
  ),
)
